/**

 * @vuelto-surface

 * mobile: ok

 * desktop: ok

 * strategy: unified

 */

import React, { useEffect } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import {
  Pressable,
  RefreshControl,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CONTENT_MAX_WIDTH_COMPACT } from '../../shared/layout/breakpoints'
import { ModalScrollScreen } from '../../shared/layout/ModalScrollScreen'
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { useNavigation, type ParamListBase } from '@react-navigation/native';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { ticketChrome } from '../../shared/ticket/ticketChrome.styles'
import { ticketColors } from '../../shared/theme/ticketColors';
import { spacing } from '../../shared/theme/spacing';
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader'
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { OperationalFilterSearchField } from '../../shared/filters/OperationalFilterPrimitives';
import { SettingsScreenBody } from '../../settings/screens/components/settingsScreenLayout';
import { BranchListSection } from './components/BranchListSection';
import { BranchSelectionHero } from './components/BranchSelectionHero';
import { BranchSelectionFooter } from './components/BranchSelectionFooter';
import { useBranchSelectionScreen } from './useBranchSelectionScreen';

// Umbral desde el que conviene mostrar el buscador (7+ sucursales).
const BRANCH_SEARCH_THRESHOLD = 7;

export const BranchSelectionScreen = () => {
  const navigation = useNavigation<any>();
  const vm = useBranchSelectionScreen(navigation);
  const { layoutWebShell } = useContentLayout('settingsWide');
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const selectionMode = vm.currentBranchId ? 'switch' : 'pick';
  const isDesktopSwitchOverlay = selectionMode === 'switch' && layoutWebShell;
  const isMobileSwitchSheet = selectionMode === 'switch' && !layoutWebShell;
  const showBranchSearch = vm.sortedBranches.length >= BRANCH_SEARCH_THRESHOLD;
  const hasSearchQuery = vm.searchQuery.trim().length > 0;

  useEffect(() => {
    if (selectionMode !== 'pick') return;
    if (vm.hasOrganization) return;
    navigation.replace('Onboarding' as never);
  }, [navigation, selectionMode, vm.hasOrganization]);

  const dismiss = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  };

  const switchFooter =
    selectionMode === 'switch' ? (
      <BranchSelectionFooter
        onCreateBranch={vm.canCreateBranch ? vm.handleCreateBranch : undefined}
        onManageBranches={vm.canManageBranches ? vm.handleManageBranches : undefined}
      />
    ) : null;

  const branchSearch = showBranchSearch ? (
    <OperationalFilterSearchField
      value={vm.searchQuery}
      onChangeText={vm.setSearchQuery}
      placeholder="Buscar sucursal"
    />
  ) : null;

  const branchList = vm.shouldRenderBranchList ? (
    <BranchListSection
      branches={vm.currentBranchId ? vm.branchesForList : vm.filteredBranches}
      cashSessionByBranch={vm.cashSessionByBranch}
      branchesLoading={vm.branchesLoading}
      emptyStateKind={vm.emptyStateKind}
      hasSearchQuery={hasSearchQuery}
      onCreateBranch={vm.handleCreateBranch}
      onSelectBranch={vm.handleSelect}
      variant={vm.currentBranchId ? 'others' : 'all'}
    />
  ) : null;

  const body = (
    <SettingsScreenBody style={styles.bodyGap}>
      <BranchSelectionHero
        mode={selectionMode}
        branch={vm.currentBranch}
        cashOpen={vm.currentBranch ? (vm.cashSessionByBranch[vm.currentBranch.id] ?? null) : null}
        branchCount={vm.sortedBranches.length}
      />

      {branchSearch}

      {branchList}
    </SettingsScreenBody>
  );

  const confirmModal = (
    <OperationalConfirmModal
      visible={vm.offlineSwitchBranchId != null}
      title="Sin conexión"
      message="Los datos pueden estar desactualizados. Cuando vuelva internet, se sincronizarán solos."
      confirmLabel="Cambiar igual"
      onClose={() => vm.setOfflineSwitchBranchId(null)}
      onConfirm={vm.confirmOfflineSwitch}
    />
  );

  const overlayScroll = (
    <ModalAwareScrollView
      style={styles.overlayScroll}
      contentContainerStyle={styles.listContent}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      refreshControl={
        <RefreshControl
          refreshing={vm.refreshing}
          onRefresh={vm.onRefresh}
          colors={[ticketColors.gold]}
          tintColor={ticketColors.gold}
        />
      }
    >
      {body}
    </ModalAwareScrollView>
  );

  if (isDesktopSwitchOverlay) {
    const dialogMaxWidth = Math.min(CONTENT_MAX_WIDTH_COMPACT, windowWidth - 48);
    const dialogMaxHeight = windowHeight * 0.88;

    return (
      <>
        <View style={styles.overlayRoot}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={dismiss}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <View
            style={[
              ticketChrome.dialogCard,
              styles.overlayDialog,
              { maxWidth: dialogMaxWidth, maxHeight: dialogMaxHeight },
            ]}
          >
            <View style={styles.overlayHeader} testID="auth.branch-selector">
              <ModalScreenHeader onClose={dismiss} title="Cambiar sucursal" />
            </View>
            {overlayScroll}
            {switchFooter}
          </View>
        </View>
        {confirmModal}
      </>
    );
  }

  if (isMobileSwitchSheet) {
    const sheetMaxHeight = windowHeight * 0.92;

    return (
      <>
        <View style={styles.sheetOverlayRoot}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={dismiss}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <View
            style={[
              ticketChrome.sheetCard,
              styles.switchSheet,
              {
                maxHeight: sheetMaxHeight,
                paddingBottom: Math.max(insets.bottom, spacing.sm),
              },
            ]}
          >
            <View style={ticketChrome.grabber} />
            <View style={styles.sheetHeader} testID="auth.branch-selector">
              <ModalScreenHeader onClose={dismiss} title="Cambiar sucursal" />
            </View>
            {overlayScroll}
            {switchFooter}
          </View>
        </View>
        {confirmModal}
      </>
    );
  }

  return (
    <View style={styles.container} testID="auth.branch-selector">
      <ModalScrollScreen
        variant={selectionMode === 'switch' ? 'form' : 'settingsWide'}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.listContent}
        refreshing={vm.refreshing}
        onRefresh={vm.onRefresh}
      >
        {body}
      </ModalScrollScreen>
      {confirmModal}
    </View>
  );
};

const styles = StyleSheet.create({
  overlayRoot: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  sheetOverlayRoot: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  overlayDialog: {
    width: '100%',
    overflow: 'hidden',
    zIndex: 1,
  },
  switchSheet: {
    width: '100%',
    zIndex: 1,
  },
  overlayHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  sheetHeader: {
    paddingHorizontal: 16,
    paddingBottom: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  overlayScroll: {
    flexGrow: 0,
    flexShrink: 1,
    backgroundColor: ticketColors.paper,
  },
  container: {
    flex: 1,
    backgroundColor: ticketColors.paper,
  },
  listContent: {
    padding: 20,
    paddingBottom: 28,
  },
  bodyGap: {
    gap: 16,
  },
});
